/*!
 * JM Live Preview — Locket add-on v1.8.1 (Birthstone Heart Locket)
 * v1.8.1: inside view only (the locket holds one photo) — Front/Inside buttons removed.
 * Loaded after jm-live-preview.js (v1.6/1.7) on products whose metafield custom.preview_layout = "locket".
 * Adds: month picker + customer photo inside the heart (drag / zoom), shown in the open locket,
 * birthstone swapped into the open locket, reflection updated, and the original + cropped photo
 * saved on the order as file properties ("Photo", "Photo (cropped)") with the crop in "_Photo crop".
 */
(function () {
  'use strict';
  var NS = window.JMLivePreview || {};
  var Preview = NS._tools && NS._tools.Preview;
  if (!Preview || Preview.prototype.__locket) return;
  Preview.prototype.__locket = true;

  function el(tag, cls, attrs) {
    var n = document.createElement(tag);
    if (cls) n.className = cls;
    if (attrs) Object.keys(attrs).forEach(function (k) { n.setAttribute(k, attrs[k]); });
    return n;
  }
  function loadImg(url) {
    return new Promise(function (resolve, reject) {
      var i = new Image();
      i.crossOrigin = 'anonymous';
      i.onload = function () { resolve(i); };
      i.onerror = reject;
      i.src = url;
    });
  }

  /* =====================================================================
   * LOCKET LAYOUT (v1.8) — Birthstone Heart Locket
   * Front view: the month photo ({photos} pattern).  Inside view: the open-locket photo with the
   * customer's own photo clipped into the heart window, the birthstone swapped to the chosen month,
   * and the mirror reflection under the locket updated to match.
   * Product metafield custom.preview_split (json), all positions as fractions of the photo:
   *  { "open": "heart-locket-open-{metal}.png",
   *    "window": [x0, y0, x1, y1], "rot": -3,           heart-shaped photo window (box + tilt in degrees)
   *    "stoneSrc": [cx, cy, r],                          stone in the month photo
   *    "stoneDst": { "gold": [cx, cy], "silver": [...] } stone in the open photo
   *    "reflect": { "gold": [axisY, x0, x1, depth], ... } mirror line under the locket
   *    "focusFront": [cx, cy, size], "focusInside": [cx, cy, size],
   *    "photoRequired": false }
   * ===================================================================== */
  var HEART = [ // unit heart, top-left (0,0) to bottom-right (1,1): start point then cubic segments
    [0.5, 0.07],
    [0.49, 0.03, 0.42, 0, 0.27, 0], [0.04, 0, 0, 0.18, 0, 0.3], [0, 0.5, 0.2, 0.72, 0.5, 1],
    [0.8, 0.72, 1, 0.5, 1, 0.3], [1, 0.18, 0.96, 0, 0.73, 0], [0.58, 0, 0.51, 0.03, 0.5, 0.07]
  ];
  function heartPath(x, b, pad) {
    // b = {x, y, w, h, rot}; drawn in the current transform, rotated about the box centre
    var X = b.x - pad, Y = b.y - pad, w = b.w + pad * 2, h = b.h + pad * 2;
    x.beginPath();
    x.moveTo(X + HEART[0][0] * w, Y + HEART[0][1] * h);
    for (var i = 1; i < HEART.length; i++) {
      var p = HEART[i];
      x.bezierCurveTo(X + p[0] * w, Y + p[1] * h, X + p[2] * w, Y + p[3] * h, X + p[4] * w, Y + p[5] * h);
    }
    x.closePath();
  }
  var lkImgCache = {};
  function lkImg(url) {
    if (!lkImgCache[url]) {
      lkImgCache[url] = loadImg(url);
      lkImgCache[url].catch(function () { delete lkImgCache[url]; });
    }
    return lkImgCache[url];
  }
  function setInputFile(input, file) {
    try {
      var dt = new DataTransfer();
      dt.items.add(file);
      input.files = dt.files;
      input.disabled = false;
      return true;
    } catch (e) { return false; }
  }

  Preview.prototype.buildLocketUI = function () {
    var self = this, lk = this.lk;
    var box = el('div', 'jmlp__photo');
    box.innerHTML =
      '<p class="jmlp__label">' + (lk.photoLabel || 'Your photo') + '</p>' +
      '<div class="jmlp__photo-row">' +
        '<button type="button" class="jmlp__btn" data-lk-add>' + (lk.addText || 'Add your photo') + '</button>' +
        '<span class="jmlp__photo-name" data-lk-name></span>' +
        '<button type="button" class="jmlp__link" data-lk-remove hidden>Remove</button>' +
      '</div>' +
      '<p class="jmlp__msg" data-lk-msg hidden>Please add the photo you\'d like inside your locket.</p>' +
      '<div class="jmlp__zoom" data-lk-zoom hidden>' +
        '<label class="jmlp__zoom-l">Zoom <input type="range" min="1" max="3" step="0.01" value="1" data-lk-range></label>' +
        '<p class="jmlp__hint jmlp__hint--left">Drag the photo in the preview to position it.</p>' +
      '</div>';
    this.stonesEl.parentNode.insertBefore(box, this.stonesEl.nextSibling);
    var q = function (s) { return box.querySelector(s); };
    this.lkUI = { root: box, name: q('[data-lk-name]'), remove: q('[data-lk-remove]'), msg: q('[data-lk-msg]'), zoom: q('[data-lk-zoom]'), range: q('[data-lk-range]') };

    // file inputs live inside the add-to-cart form(s) so the theme's FormData sends them to the cart
    this.lkFiles = this.forms.map(function (form, i) {
      var wrap = el('div', 'jmlp__files');
      wrap.hidden = true;
      var orig = el('input', '', { type: 'file', accept: 'image/*', name: 'properties[Photo]' });
      var crop = el('input', '', { type: 'file', name: 'properties[Photo (cropped)]' });
      crop.disabled = true;
      if (i > 0) orig.disabled = true;
      wrap.appendChild(orig); wrap.appendChild(crop);
      form.appendChild(wrap);
      return { orig: orig, crop: crop };
    });
    var picker = this.lkFiles[0] && this.lkFiles[0].orig;
    if (!picker) { picker = el('input', '', { type: 'file', accept: 'image/*', hidden: '' }); box.appendChild(picker); }
    picker.disabled = false;
    this.lkPicker = picker;
    q('[data-lk-add]').addEventListener('click', function () { picker.click(); });
    picker.addEventListener('change', function () {
      var f = picker.files && picker.files[0];
      if (f) self.setPhoto(f);
    });
    this.lkUI.remove.addEventListener('click', function () { self.clearPhoto(); });
    this.lkUI.range.addEventListener('input', function () {
      self.state.crop.z = parseFloat(self.lkUI.range.value) || 1;
      self.drawLocket(); self.lkChanged();
    });
    this.bindLocketDrag();
    if (lk.hideOtherUploads !== false) this.hideOtherUploads();
  };

  // The product may also have an app upload field (e.g. King Product Options): hide it so there is one upload.
  Preview.prototype.hideOtherUploads = function () {
    var self = this;
    function run() {
      Array.prototype.forEach.call(document.querySelectorAll('input[type="file"]'), function (inp) {
        if (self.lkFiles.some(function (f) { return f.orig === inp || f.crop === inp; }) || inp === self.lkPicker) return;
        var n = inp.closest('[class*="ymq"]');
        if (!n) return;
        // climb to the option wrapper that holds only this upload
        while (n.parentElement && /ymq/.test(n.parentElement.className || '') &&
               n.parentElement.querySelectorAll('input, select, textarea').length === n.querySelectorAll('input, select, textarea').length) n = n.parentElement;
        n.style.display = 'none';
        n.setAttribute('data-jmlp-hidden-upload', '');
      });
    }
    run(); setTimeout(run, 1200); setTimeout(run, 3500);
  };

  Preview.prototype.setView = function (v) {
    this.state.view = 'inside'; // the locket holds one photo, so the preview always shows it open
    this.stage.classList.toggle('jmlp__stage--drag', this.state.view === 'inside' && !!this.state.photo);
    this.render();
    this.activateSlide();
  };

  Preview.prototype.setPhoto = function (file) {
    var self = this;
    if (!/^image\//.test(file.type || 'image/')) return;
    var url = URL.createObjectURL(file);
    loadImg(url).then(function (img) {
      if (self.state.photo && self.state.photo.url) URL.revokeObjectURL(self.state.photo.url);
      self.state.photo = { img: img, url: url, name: file.name, file: file };
      self.state.crop = { x: 0, y: 0, z: 1 };
      self.lkUI.range.value = 1;
      self.lkUI.name.textContent = file.name;
      self.lkUI.remove.hidden = false;
      self.lkUI.zoom.hidden = false;
      self.lkUI.msg.hidden = true;
      // copy the original into the other forms (sticky add-to-cart bar)
      self.lkFiles.forEach(function (f) { if (f.orig !== self.lkPicker) setInputFile(f.orig, file); });
      self.setView('inside');
      self.lkChanged();
    }).catch(function () {
      URL.revokeObjectURL(url);
      self.lkUI.name.textContent = 'Sorry, that photo could not be opened — please try a JPG or PNG.';
    });
  };

  Preview.prototype.clearPhoto = function () {
    if (this.state.photo && this.state.photo.url) URL.revokeObjectURL(this.state.photo.url);
    this.state.photo = null;
    this.lkUI.name.textContent = '';
    this.lkUI.remove.hidden = true;
    this.lkUI.zoom.hidden = true;
    this.lkFiles.forEach(function (f) {
      try { f.orig.value = ''; } catch (e) {}
      try { f.crop.value = ''; } catch (e) {}
      f.crop.disabled = true;
    });
    if (this.lkPicker) this.lkPicker.disabled = false;
    this.lkFiles.forEach(function (f, i) { if (i > 0) f.orig.disabled = true; });
    this.setView('inside');
    this.writeProps();
  };

  // after a photo / crop change: refresh cart properties now, the cropped file a moment later
  Preview.prototype.lkChanged = function () {
    var self = this;
    this.writeProps();
    clearTimeout(this._lkT);
    this._lkT = setTimeout(function () { self.exportCrop(); }, 350);
  };

  Preview.prototype.windowBox = function (W, H) {
    var w = this.lk.window || [0.43, 0.65, 0.56, 0.78];
    return { x: w[0] * W, y: w[1] * H, w: (w[2] - w[0]) * W, h: (w[3] - w[1]) * H, rot: (this.lk.rot || 0) * Math.PI / 180 };
  };

  // where the customer's photo sits: covers the window at zoom 1, never leaves a gap
  Preview.prototype.photoPlacement = function (b) {
    var p = this.state.photo, c = this.state.crop;
    var iw = p.img.naturalWidth, ih = p.img.naturalHeight;
    var sc = Math.max(b.w / iw, b.h / ih) * c.z;
    var dw = iw * sc, dh = ih * sc;
    var mx = Math.max(0, (dw - b.w) / 2 / b.w), my = Math.max(0, (dh - b.h) / 2 / b.h);
    c.x = Math.max(-mx, Math.min(mx, c.x)); c.y = Math.max(-my, Math.min(my, c.y));
    return { sc: sc, dw: dw, dh: dh, cx: b.x + b.w / 2 + c.x * b.w, cy: b.y + b.h / 2 + c.y * b.h };
  };

  Preview.prototype.renderLocket = function (token) {
    var self = this, metal = this.state.metal || 'gold', mIdx = this.state.stones[0];
    var front = this.fileUrl(this.cfg.photos, metal, mIdx, 2048);
    var jobs = [lkImg(front)];
    if (this.state.view === 'inside') jobs.push(lkImg(this.fileUrl(this.lk.open || '', metal, mIdx, 2048)));
    // warm the cache for the other months / the open photo
    setTimeout(function () {
      lkImg(self.fileUrl(self.lk.open || '', metal, mIdx, 2048)).catch(function () {});
    }, 400);
    return Promise.all(jobs).then(function (res) {
      if (token !== self.renderToken) return;
      self.lkImgs = { front: res[0], open: res[1] || null, metal: metal };
      self.drawLocket();
    });
  };

  Preview.prototype.drawLocket = function () {
    var I = this.lkImgs;
    if (!I) return;
    var lk = this.lk, inside = this.state.view === 'inside' && I.open;
    var base = inside ? I.open : I.front;
    var W = base.naturalWidth, H = base.naturalHeight;
    var cv = this.canvas;
    if (!cv) { cv = this.canvas = el('canvas', 'jmlp__canvas', { role: 'img' }); this.stage.insertBefore(cv, this.layersEl); }
    if (cv.width !== W || cv.height !== H) { cv.width = W; cv.height = H; }
    var x = cv.getContext('2d');
    x.drawImage(base, 0, 0, W, H);
    var label = this.recipe().stones.map(function (s) { return s.label; }).join(', ');
    if (inside) {
      // 1) birthstone: cut the stone from the month photo and set it into the open locket
      var ss = lk.stoneSrc, sd = lk.stoneDst && (lk.stoneDst[I.metal] || lk.stoneDst.gold);
      if (ss && sd) {
        var fW = I.front.naturalWidth, fH = I.front.naturalHeight, r = ss[2] * fW, R = Math.ceil(r * 1.25);
        var tmp = document.createElement('canvas'); tmp.width = tmp.height = R * 2;
        var t = tmp.getContext('2d');
        t.drawImage(I.front, ss[0] * fW - R, ss[1] * fH - R, R * 2, R * 2, 0, 0, R * 2, R * 2);
        var g = t.createRadialGradient(R, R, r * 0.72, R, R, r * 1.02);
        g.addColorStop(0, 'rgba(0,0,0,1)'); g.addColorStop(1, 'rgba(0,0,0,0)');
        t.globalCompositeOperation = 'destination-in'; t.fillStyle = g; t.fillRect(0, 0, R * 2, R * 2);
        var k = W / fW;
        x.drawImage(tmp, sd[0] * W - R * k, sd[1] * H - R * k, R * 2 * k, R * 2 * k);
      }
      // 2) the customer's photo inside the heart
      if (this.state.photo) {
        var b = this.windowBox(W, H), pl = this.photoPlacement(b), pad = W * 0.0028;
        x.save();
        x.translate(b.x + b.w / 2, b.y + b.h / 2); x.rotate(b.rot); x.translate(-(b.x + b.w / 2), -(b.y + b.h / 2));
        heartPath(x, b, pad); x.clip();
        x.fillStyle = '#fff'; x.fillRect(b.x - pad, b.y - pad, b.w + pad * 2, b.h + pad * 2);
        x.drawImage(this.state.photo.img, pl.cx - pl.dw / 2, pl.cy - pl.dh / 2, pl.dw, pl.dh);
        // soft inner edge so the photo sits under the rim
        x.lineWidth = W * 0.005; x.strokeStyle = 'rgba(60,40,10,.22)';
        heartPath(x, b, pad); x.stroke();
        x.lineWidth = W * 0.0015; x.strokeStyle = 'rgba(0,0,0,.25)';
        heartPath(x, b, pad); x.stroke();
        x.restore();
        label += ' — with your photo inside';
      }
      // 3) reflection: change it by the same amount the locket changed, mirrored
      this.lkReflect(x, I, W, H);
    }
    cv.setAttribute('aria-label', label);
    var fz = (inside ? lk.focusInside : lk.focusFront) || [0.5, 0.7, 0.5];
    var fs = Math.min(W, H, W * fz[2]);
    this.focus = { x: Math.max(0, Math.min(W - fs, W * fz[0] - fs / 2)), y: Math.max(0, Math.min(H - fs, H * fz[1] - fs / 2)), s: fs };
    this.paintCopies();
    this.stage.classList.add('jmlp__stage--canvas');
  };

  Preview.prototype.lkReflect = function (x, I, W, H) {
    var rf = this.lk.reflect && (this.lk.reflect[I.metal] || this.lk.reflect.gold);
    if (!rf) return;
    try {
      var Y = Math.round(rf[0] * H), x0 = Math.round(rf[1] * W), x1 = Math.round(rf[2] * W);
      var D = Math.min(Math.round(rf[3] * H), Y, H - Y), w = x1 - x0;
      var a0 = this.lk.reflectAlpha || 0.52, tau = (this.lk.reflectFade || 0.21) * H;
      var key = I.metal + ':' + W;
      this.lkOrig = this.lkOrig || {};
      if (!this.lkOrig[key]) {
        var o = document.createElement('canvas'); o.width = W; o.height = H;
        var oc = o.getContext('2d'); oc.drawImage(I.open, 0, 0, W, H);
        this.lkOrig[key] = { above: oc.getImageData(x0, Y - D, w, D).data, below: oc.getImageData(x0, Y, w, D) };
      }
      var orig = this.lkOrig[key];
      var cur = x.getImageData(x0, Y - D, w, D).data;
      var out = new ImageData(new Uint8ClampedArray(orig.below.data), w, D), od = out.data;
      for (var d = 0; d < D; d++) {
        var a = a0 * Math.exp(-d / tau), src = (D - 1 - d) * w * 4, dst = d * w * 4;
        for (var i = 0; i < w * 4; i += 4) {
          var s = src + i, t = dst + i;
          od[t] += a * (cur[s] - orig.above[s]);
          od[t + 1] += a * (cur[s + 1] - orig.above[s + 1]);
          od[t + 2] += a * (cur[s + 2] - orig.above[s + 2]);
        }
      }
      x.putImageData(out, x0, Y);
    } catch (e) {}
  };

  // drag the photo inside the heart (mouse, pen or finger)
  Preview.prototype.bindLocketDrag = function () {
    var self = this, st = this.stage, drag = null;
    st.addEventListener('pointerdown', function (e) {
      if (self.state.view !== 'inside' || !self.state.photo || !self.canvas) return;
      var r = self.canvas.getBoundingClientRect();
      drag = { x: e.clientX, y: e.clientY, k: self.canvas.width / r.width, c: { x: self.state.crop.x, y: self.state.crop.y } };
      try { st.setPointerCapture(e.pointerId); } catch (er) {}
      e.preventDefault();
    });
    st.addEventListener('pointermove', function (e) {
      if (!drag) return;
      var b = self.windowBox(self.canvas.width, self.canvas.height);
      self.state.crop.x = drag.c.x + (e.clientX - drag.x) * drag.k / b.w;
      self.state.crop.y = drag.c.y + (e.clientY - drag.y) * drag.k / b.h;
      self.drawLocket();
    });
    function end() { if (drag) { drag = null; self.lkChanged(); } }
    st.addEventListener('pointerup', end);
    st.addEventListener('pointercancel', end);
  };

  // the part of the photo that shows in the heart, as its own image file for the workshop
  Preview.prototype.exportCrop = function () {
    var self = this, p = this.state.photo;
    if (!p || !this.lkImgs) return;
    var base = this.lkImgs.open || this.lkImgs.front, W = base.naturalWidth, H = base.naturalHeight;
    var b = this.windowBox(W, H), pl = this.photoPlacement(b);
    var sw = b.w / pl.sc, sh = b.h / pl.sc;
    var sx = p.img.naturalWidth / 2 - (pl.cx - b.x - b.w / 2) / pl.sc - sw / 2;
    var sy = p.img.naturalHeight / 2 - (pl.cy - b.y - b.h / 2) / pl.sc - sh / 2;
    var ow = Math.round(Math.min(sw, 1600)), oh = Math.round(ow * sh / sw);
    var c = document.createElement('canvas'); c.width = ow; c.height = oh;
    c.getContext('2d').drawImage(p.img, sx, sy, sw, sh, 0, 0, ow, oh);
    c.toBlob(function (blob) {
      if (!blob || self.state.photo !== p) return;
      var nm = (p.name || 'photo').replace(/\.[^.]+$/, '') + '-cropped.jpg';
      var file = new File([blob], nm, { type: 'image/jpeg' });
      self.lkFiles.forEach(function (f) { setInputFile(f.crop, file); });
    }, 'image/jpeg', 0.92);
  };

  /* ----- hook into the core preview ----- */
  var P = Preview.prototype;
  var coreRender = P.render, coreSwatch = P.swatchFor, coreProps = P.writeProps, coreValidate = P.validate;

  P.render = function () {
    if (!this.locket) return coreRender.apply(this, arguments);
    var self = this, token = ++this.renderToken;
    this.loading.hidden = false;
    this.renderLocket(token).catch(function (e) {
      if (window.Shopify && window.Shopify.designMode) console.warn('[JM Live Preview locket]', e);
      self.stage.classList.remove('jmlp__stage--canvas');
      if (self.cfg.fallbackImage) self.base.src = self.cfg.fallbackImage;
    }).then(function () { if (token === self.renderToken) self.loading.hidden = true; });
    this.writeProps();
    this.root.dispatchEvent(new CustomEvent('jmlp:change', { bubbles: true, detail: this.recipe() }));
  };

  P.swatchFor = function (mIdx) {
    if (!this.locket) return coreSwatch.apply(this, arguments);
    var m = this.months[mIdx];
    if (!m) return Promise.resolve(null);
    var metal = this.state.metal || 'gold', ck = 'lk:' + metal + ':' + m.key, ss = this.lk.stoneSrc || [0.5, 0.5, 0.01];
    this.swatchCache = this.swatchCache || {};
    if (!this.swatchCache[ck]) {
      this.swatchCache[ck] = lkImg(this.fileUrl(this.cfg.photos, metal, mIdx, 2048)).then(function (img) {
        var W = img.naturalWidth, s = ss[2] * W * 2.6;
        var c = document.createElement('canvas'); c.width = c.height = 96;
        c.getContext('2d').drawImage(img, ss[0] * W - s / 2, ss[1] * img.naturalHeight - s / 2, s, s, 0, 0, 96, 96);
        return c.toDataURL('image/jpeg', 0.88);
      }).catch(function () { return null; });
    }
    return this.swatchCache[ck];
  };

  P.writeProps = function () {
    coreProps.apply(this, arguments);
    if (!this.locket || !this.state.photo || !this.propsBoxes) return;
    var cr = this.state.crop, v = [cr.x.toFixed(3), cr.y.toFixed(3), cr.z.toFixed(2)].join(',');
    this.propsBoxes.forEach(function (box) {
      var i = document.createElement('input');
      i.type = 'hidden'; i.name = 'properties[_Photo crop]'; i.value = v;
      box.appendChild(i);
    });
  };

  P.validate = function () {
    if (this.locket && this.lk.photoRequired && !this.state.photo && this.lkUI) {
      this.lkUI.msg.hidden = false;
      this.lkUI.root.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return false;
    }
    return coreValidate.apply(this, arguments);
  };

  /* ----- switch on for preview roots set to the locket layout ----- */
  function initLocket(inst) {
    if (!inst || inst.locket || !inst.cfg || inst.cfg.layout !== 'locket') return;
    inst.locket = true;
    inst.split = false; inst.countIdx = -1; inst.cfg.maxStones = 1;
    inst.lk = inst.cfg.splitCfg || {};
    inst.photoMode = !!inst.cfg.photos;
    inst.state.count = 1;
    inst.state.photo = null; inst.state.view = 'inside'; inst.state.crop = { x: 0, y: 0, z: 1 };
    inst.swatchCache = {};
    if (inst.countWrap) inst.countWrap.hidden = true;
    inst.buildLocketUI();
    inst.buildRows();
    inst.render();
  }
  function scan() {
    document.querySelectorAll('[data-jmlp]').forEach(function (root) { initLocket(root.__jmlp); });
  }
  scan();
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { setTimeout(scan, 0); });
  window.addEventListener('load', scan);
  document.addEventListener('shopify:section:load', function () { setTimeout(scan, 0); });
})();
